import { useCallback, useEffect, useRef } from 'react'
import { trackPaywallEvent, claimSessionImpression } from '../services/paywall-analytics'
import { consumeFreshAgentOrigin } from '../services/paywall-agent-origin'

// #1437: at least half the card has to be on screen (not just present in the
// DOM) before it counts as seen -- a sliver peeking in from an edge should
// not count the same as the whole card.
const VISIBILITY_THRESHOLD = 0.5
// ...and it has to stay that visible for a full second, so a fast scroll-past
// (the card crosses the 50% mark for one frame on the way to somewhere else)
// does not register either. Both numbers are a starting point, not a
// contract -- tune them if real dwell-time data says otherwise.
const VISIBILITY_DWELL_MS = 1000

// One showing of the paywall (one impression plus its N cta clicks) needs an
// id that groups them, since `session_id` spans a whole login and
// `occurred_at - dwell_ms` back-derivation is fragile. `crypto.randomUUID`
// is preferred, but is missing in some test environments (older jsdom) and
// in non-secure contexts (the Crypto interface only exposes `randomUUID` on
// HTTPS/localhost), so this falls back to a hand-rolled v4-shaped string.
// The shape has to be right either way: the API validator nulls anything
// that does not match the v4 pattern.
const generateEventId = () => {
  // eslint-disable-next-line no-undef
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    // eslint-disable-next-line no-undef
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.random() * 16 | 0
    const value = char === 'x' ? random : (random & 0x3 | 0x8)
    return value.toString(16)
  })
}

/*
 * Owns everything the paywall surfaces (the upgrade modal, the full-page
 * limit screen, and the dashboard banner) used to duplicate: the mount-once
 * guard, the dwell clock, firing the impression, and a `trackCta` that
 * stamps `dwell_ms` measured from that instance's own impression.
 *
 * `enabled: false` makes both the impression and every `trackCta` call a
 * no-op -- this is what keeps a non-upgrade card (e.g. the dashboard's
 * youtubeRed card) out of the dataset. `banner_impression`/`paywall_impression`
 * are ATTRIBUTABLE events (last-touch), so a card that should not count as a
 * paywall touch must emit nothing at all, not an event that gets filtered
 * downstream.
 *
 * Analytics must never break a render: every call into `trackPaywallEvent` is
 * wrapped in try/catch so a storage/JSON edge case surfaces as a dropped
 * event, not an unmounted tree.
 */
const usePaywallTracking = ({
  surface,
  paywallType,
  featureName,
  impressionEvent,
  ctaEvent,
  props,
  enabled = true,
  // Opt-in: mount alone no longer counts as "seen" for the caller that sets
  // this. When on, the impression waits for `visibilityRef` (returned below)
  // to be attached to the element that has to actually be on screen, and
  // fires once it has held VISIBILITY_THRESHOLD/VISIBILITY_DWELL_MS. Off by
  // default so every existing caller (the upgrade modal chief among them,
  // where opening IS being seen -- it covers the screen) keeps firing on
  // mount exactly as before.
  requireVisibility = false,
  // Opt-in for surfaces that are ALWAYS on screen -- the dashboard banner
  // strip and the header upgrade button. For those, one impression per render
  // is just a page view under another name: the element is unconditionally
  // visible, so "was it seen" carries no information beyond "did they open the
  // page". Deduping to one per login session makes an impression mean a
  // distinct exposure again, which is what unique reach and click-through
  // actually need. Paywall modals deliberately do NOT set this: each opening
  // of a modal is a genuinely separate exposure.
  oncePerSession = false,
  // What to dedupe on within the surface -- the banner card key, say -- so two
  // different cards on the same surface are not collapsed into one.
  sessionDedupeKey = null
}) => {
  const loggedRef = useRef(false)
  const shownAtRef = useRef(null)
  const eventIdRef = useRef(null)
  // Only meaningful when requireVisibility is on; harmless and unused
  // otherwise. The caller attaches this to its outermost element.
  const visibilityRef = useRef(null)

  // `trackCta` needs the *current* render's values (UpgradePlanBody's `type`
  // can change while the component stays mounted) but must keep a stable
  // identity across renders, so the latest inputs live in a ref rather than a
  // closure captured once.
  const latestRef = useRef(null)
  latestRef.current = { surface, paywallType, featureName, ctaEvent, props, enabled }

  useEffect(() => {
    if (!enabled) return
    if (loggedRef.current) return

    // Shared by both paths below. Guarded exactly as before: analytics must
    // never break a render, and this must be safe to call more than once
    // (the visibility path can end up scheduling it from more than one
    // intersection) without logging twice -- loggedRef is the single source
    // of truth for "already fired this mount".
    const fireImpression = () => {
      if (loggedRef.current) return
      // Marked before the send, not after: a second mount in the same session
      // must be suppressed even if the send itself is still buffered.
      if (oncePerSession && !claimSessionImpression(surface, sessionDedupeKey)) {
        loggedRef.current = true
        return
      }
      loggedRef.current = true
      shownAtRef.current = Date.now()
      eventIdRef.current = generateEventId()
      try {
        // Consumed unconditionally (fresh or not) so it can only ever attach
        // to this one impression -- see paywall-agent-origin.js.
        const agentOrigin = consumeFreshAgentOrigin()
        trackPaywallEvent({
          eventName: impressionEvent,
          surface,
          paywallType,
          featureName,
          eventId: eventIdRef.current,
          props: agentOrigin ? { ...props, origin: agentOrigin } : props
        })
      } catch (error) {
        // Analytics must never break a render.
      }
    }

    if (!requireVisibility) {
      fireImpression()
      return
    }

    const element = visibilityRef.current
    // No element to watch (the caller forgot the ref, or requireVisibility
    // flipped on before the first paint) and no IntersectionObserver to
    // watch it with (jsdom in tests, very old browsers) both fall back to
    // firing on mount: losing the event entirely would be a worse trade than
    // the over-count this whole fix exists to remove.
    if (!element || typeof IntersectionObserver === 'undefined') {
      fireImpression()
      return
    }

    let dwellTimer = null
    let observer

    try {
      // eslint-disable-next-line no-undef
      observer = new IntersectionObserver((entries) => {
        const entry = entries.find((candidate) => candidate.target === element) || entries[0]
        if (!entry) return
        if (entry.isIntersecting) {
          // Already counting down from an earlier callback in the same
          // visible stretch -- do not restart the clock.
          if (dwellTimer) return
          dwellTimer = setTimeout(() => {
            dwellTimer = null
            fireImpression()
            // Nothing left to watch once it has fired: scrolling the card
            // out and back must not schedule a second one.
            observer.disconnect()
          }, VISIBILITY_DWELL_MS)
        } else if (dwellTimer) {
          // Left before the dwell completed -- a fast scroll-past, not a
          // real view. Re-entering starts a fresh countdown, not a resumed
          // one.
          clearTimeout(dwellTimer)
          dwellTimer = null
        }
      }, { threshold: VISIBILITY_THRESHOLD })
      observer.observe(element)
    } catch (error) {
      // Same trade as the missing-API case above: never let this throw into
      // a render, and never let it mean no event at all.
      fireImpression()
      return
    }

    return () => {
      if (dwellTimer) clearTimeout(dwellTimer)
      observer.disconnect()
    }
    // Deliberately just `enabled`/`requireVisibility`: `loggedRef` already
    // makes firing idempotent, so re-running on every prop change would be
    // redundant work -- and for the visibility path, actively harmful, since
    // tearing down and rebuilding the observer on an unrelated prop change
    // would restart a dwell timer that was already most of the way there.
    // The one case worth reacting to is `enabled` flipping from false to
    // true after mount, which is why it stays in the dependency array.
  }, [enabled, requireVisibility, oncePerSession, sessionDedupeKey])

  const trackCta = useCallback((ctaId, extraProps) => {
    const current = latestRef.current
    if (!current.enabled) return
    try {
      trackPaywallEvent({
        eventName: current.ctaEvent,
        surface: current.surface,
        paywallType: current.paywallType,
        featureName: current.featureName,
        ctaId,
        eventId: eventIdRef.current,
        props: {
          ...current.props,
          ...extraProps,
          dwell_ms: shownAtRef.current ? Date.now() - shownAtRef.current : null
        }
      })
    } catch (error) {
      // Analytics must never break a render.
    }
  }, [])

  return { trackCta, visibilityRef }
}

export default usePaywallTracking
